import React from 'react'
import PropTypes from 'prop-types'
import Modal from 'react-modal'
import { formatDate, parseDate } from 'react-day-picker/moment'
import DayPickerInput from 'react-day-picker/DayPickerInput'
import update from 'immutability-helper'

import InputCheckbox from 'Pods/Shared/InputCheckbox'
import Icon from 'Pods/Shared/Icon/Icon'
import { run, RuleRunner } from 'Pods/Shared/RuleRunner'
import * as ValidationRules from 'Pods/Shared/Rules/Rules'
import PickerClasses from 'Pods/Shared/DatePickClassNames'

class ExtendDeadlineModal extends React.Component {
  constructor() {
    super()

    this.state = {
      isSubmitDisabled: false,
      deadline: '',
      isEndless: false,
      showErrors: false,
      validationErrors: {},
      isEdited: false,
    }

    this.onModalClose = this.onModalClose.bind(this)
    this.onSubmitSuccess = this.onSubmitSuccess.bind(this)
    this.onSubmitFailure = this.onSubmitFailure.bind(this)
    this.onDayChange = this.onDayChange.bind(this)
    this.toggleChecked = this.toggleChecked.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
  }

  fieldValidations() {
    const errors = []

    if (!this.state.isEndless) {
      errors.push(RuleRunner('deadline', 'Deadline', ValidationRules.required))
    }

    return (errors)
  }

  isFormGroupValid(field) {
    return this.state.showErrors && this.state.validationErrors[field] ? ' is-invalid' : ''
  }

  errorFor(field) {
    return this.state.validationErrors[field] || ''
  }

  onDayChange(value = '') {
    const newState = update(this.state, { deadline: { $set: value }, isEndless: { $set: false } })

    newState.validationErrors = run(newState, this.fieldValidations())
    newState.isEdited = true

    this.setState(newState)
  }

  toggleChecked() {
    this.setState((prevState) => {
      return ({
        isEdited: true,
        isEndless: !prevState.isEndless,
        deadline: '',
      })
    })
  }

  handleSubmit(e) {
    e.preventDefault()
    const currentState = this.state
    const newState = this.state
    const params = {
      data: {
        attributes: {
          deadline: currentState.isEndless ? '' : currentState.deadline,
          endless: currentState.isEndless,
        },
      },
    }

    newState.validationErrors = run(currentState, this.fieldValidations())
    newState.showErrors = true

    this.setState(newState)

    if (Object.getOwnPropertyNames(newState.validationErrors).length === 0) {
      this.setState({ isSubmitDisabled: true })

      axios.put(`/api/internal/jobs/${this.props.id}/update-deadline`, params)
        .then(this.onSubmitSuccess).catch(this.onSubmitFailure)
    }
  }

  onSubmitSuccess() {
    this.setState({ isSubmitDisabled: false })
    App.events.publish('app:notification:flash:success', 'The Project deadline was changed successfully')
    setTimeout(() => window.location.assign(window.location.pathname), 3000)
  }

  onSubmitFailure() {
    this.setState({ isSubmitDisabled: false })
    App.events.publish('app:notification:flash:error', 'Something went wrong, please try again')
  }

  onModalClose(e) {
    this.setState({ isSubmitDisabled: false })
    this.props.closeModal(e)
  }

  render() {
    return (
      <Modal
        isOpen
        shouldCloseOnOverlayClick
        onRequestClose={this.onModalClose}
        contentLabel="Modal"
        ariaHideApp={false}
        className={{
          base: '',
          afterOpen: 'overlay-modal--container_after-open overlay-modal-container--xs',
          beforeClose: 'overlay-modal--container_before-close',
        }}
        overlayClassName={{
          base: 'overlay-modal--background',
          afterOpen: 'overlay-modal--background_after-open',
          beforeClose: 'overlay-modal--background_before-close',
        }}
      >
        <div className="overlay-modal" onClick={(e) => e.stopPropagation()}>
          <button className="btn btn-transparent overlay-modal--close-inner" onClick={this.onModalClose}>
            <Icon type={['fal', 'times']} color="grey-dark" />
          </button>
          <div className="overlay-modal--content overlay-modal--deadline">
            <div className="l-inner-large">
              <h2 className="modal-title">Edit deadline</h2>
              <p className="heading-18">Deadline</p>
              <div className={`row form-group form-group-grey create-project__deadline${this.state.isEndless ? '' : this.isFormGroupValid('deadline')}`}>
                <div className="col-12">
                  <div className="form-group--with-prefix">
                    <div className="form-group-prepend">
                      <span className={`form-group-text ${this.state.showErrors}`}>
                        <Icon type={['far', 'calendar']} notSpaced />
                      </span>
                    </div>

                    <DayPickerInput
                      classNames={PickerClasses.PickerInput}
                      dayPickerProps={{
                        classNames: PickerClasses.DatePickerClasses,
                        disabledDays: { before: new Date() },
                      }}
                      format="YYYY-MM-DD"
                      placeholder="YYYY-MM-DD"
                      parseDate={parseDate}
                      formatDate={formatDate}
                      inputProps={{ name: 'deadline' }}
                      onDayChange={this.onDayChange}
                      value={this.state.deadline}
                    />
                    <span className="day-picker__infinite-checkbox">
                      <InputCheckbox
                        id="isEndless"
                        label="No fixed deadline"
                        checked={this.state.isEndless}
                        onChange={this.toggleChecked}
                      />
                    </span>
                  </div>
                  {this.state.showErrors && this.errorFor('deadline') && !this.state.isEndless && (
                    <div className="error-message">
                      <span className="text">{this.errorFor('deadline')}</span>
                    </div>
                  )}
                </div>
              </div>
              <p className="modal-description">
                <span>Note:</span>
                If you proceed, applicant(s) will be notified about the deadline change. You won't be able to pay and
                start working until the selected designer accepts your updated offer or edits application.
              </p>
              <div className="footer u-text-right u-mt10">
                <button className="btn btn-fat btn-outline-default" onClick={this.onModalClose}>Cancel</button>
                <button
                  type="submit"
                  className="btn btn-fat btn-primary u-ml10"
                  disabled={this.state.isSubmitDisabled || !this.state.isEdited}
                  onClick={this.handleSubmit}
                >
                  Edit deadline
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    )
  }
}

ExtendDeadlineModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  modalIsOpen: PropTypes.bool,
}

ExtendDeadlineModal.defaultProps = {
  modalIsOpen: false,
}

export default ExtendDeadlineModal
